// src/pages/admin/reviews/AdminReviewList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { del, get } from "../../../lib/http";
import { API } from "../../../config/api";
import FancyLoader from "../../../components/FancyLoader";
import { AD } from "../../adminUi.js";

/* ----------------------------- helpers ----------------------------- */
const cn = (...a) => a.filter(Boolean).join(" ");
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

function Star({ filled }) {
  return (
    <svg
      className={cn("h-4 w-4", filled ? "text-amber-500" : "text-slate-300")}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.787 1.402 8.171L12 18.896 4.664 23.168l1.402-8.171L.132 9.21l8.2-1.192L12 .587z" />
    </svg>
  );
}

function Stars({ value = 0 }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} filled={i < Number(value || 0)} />
      ))}
    </div>
  );
}

/* --------------------------- main component --------------------------- */
export default function AdminReviewList() {
  const [reviews, setReviews] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // filters / sort / search
  const [q, setQ] = useState("");
  const [rating, setRating] = useState("all"); // all | 5..1
  const [sort, setSort] = useState("new"); // new | old | high | low

  // pagination (client-side)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // confirm modal
  const [confirm, setConfirm] = useState({ open: false, id: null });

  /* fetch */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setFetching(true);
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(API.review.getAllReviews, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (alive && Array.isArray(res?.reviews)) setReviews(res.reviews);
      } catch (err) {
        console.error("getAllReviews error:", err);
        if (alive) setReviews([]);
      } finally {
        if (alive) setFetching(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* view derive */
  const filtered = useMemo(() => {
    let list = [...reviews];

    // search by user name or comment
    const term = q.trim().toLowerCase();
    if (term) {
      list = list.filter((r) =>
        [r?.userId?.name, r?.comment]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term))
      );
    }

    // rating filter
    if (rating !== "all") {
      list = list.filter((r) => Number(r?.rating) === Number(rating));
    }

    // sort
    list.sort((a, b) => {
      const ad = new Date(a?.createdAt || 0).getTime();
      const bd = new Date(b?.createdAt || 0).getTime();
      const ar = Number(a?.rating || 0);
      const br = Number(b?.rating || 0);
      switch (sort) {
        case "new":
          return bd - ad;
        case "old":
          return ad - bd;
        case "high":
          return br - ar;
        case "low":
          return ar - br;
        default:
          return 0;
      }
    });

    return list;
  }, [reviews, q, rating, sort]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const paged = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  useEffect(() => {
    // reset page when filters change
    setPage(1);
  }, [q, rating, sort, pageSize]);

  /* actions */
  const handleDelete = async (id) => {
    setConfirm({ open: false, id: null });
    try {
      setBusyId(id);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const res = await del(`${API.review.deleteReviews}/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      if (res?.success) {
        setReviews((prev) => prev.filter((r) => r._id !== id));
      } else {
        alert(res?.message || "Failed to delete review.");
      }
    } catch (err) {
      console.error("deleteReview error:", err);
      alert("Something went wrong while deleting.");
    } finally {
      setBusyId(null);
    }
  };

  /* UI */
  return (
    <div className={AD.page}>
      {/* Title */}
      <div className={cn(AD.headerRow, "items-start md:items-end")}>
        <div>
          <h1 className={AD.title}>Reviews</h1>
          <p className={AD.subtitle}>
            Moderate and manage user feedback
          </p>
        </div>

        <div className="hidden md:flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400">
          <Chip label="Total" value={reviews.length} />
          <Chip
            label="Avg"
            value={
              reviews.length
                ? (reviews.reduce((s, r) => s + Number(r?.rating || 0), 0) / reviews.length).toFixed(1)
                : "—"
            }
          />
        </div>
      </div>

      {/* Toolbar */}
      <div className={cn("sticky top-2 z-10", AD.filterCard)}>
        <div className="grid gap-3 md:grid-cols-5">
          {/* search */}
          <label className="md:col-span-2 block">
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Search</span>
            <div className="relative mt-1">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by user name or comment…"
                className={AD.input}
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-3.5-3.5" />
              </svg>
            </div>
          </label>

          {/* rating */}
          <Select
            label="Rating"
            value={rating}
            onChange={setRating}
            options={[
              { label: "All", value: "all" },
              { label: "5 ★", value: "5" },
              { label: "4 ★", value: "4" },
              { label: "3 ★", value: "3" },
              { label: "2 ★", value: "2" },
              { label: "1 ★", value: "1" },
            ]}
          />

          {/* sort */}
          <Select
            label="Sort"
            value={sort}
            onChange={setSort}
            options={[
              { label: "Newest", value: "new" },
              { label: "Oldest", value: "old" },
              { label: "Rating: High → Low", value: "high" },
              { label: "Rating: Low → High", value: "low" },
            ]}
          />

          {/* page size */}
          <Select
            label="Rows / page"
            value={String(pageSize)}
            onChange={(v) => setPageSize(Number(v))}
            options={[
              { label: "10", value: "10" },
              { label: "20", value: "20" },
              { label: "50", value: "50" },
            ]}
          />
        </div>
      </div>

      {/* Content */}
      <div className="rounded-2xl border bg-white dark:bg-gray-900 dark:border-gray-800 shadow-sm overflow-hidden">
        {/* header row */}
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800 text-[12px] font-semibold text-slate-600 dark:text-slate-300">
          <div className="col-span-3">User</div>
          <div className="col-span-2">Rating</div>
          <div className="col-span-4">Comment</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {/* body */}
        {fetching ? (
          <div className="px-4 py-10">
            <FancyLoader label="Processing..." />
            {/* skeleton rows */}
            <div className="mt-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
              ))}
            </div>
          </div>
        ) : paged.length ? (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {paged.map((r) => (
              <li key={r._id} className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-amber-50/40 dark:hover:bg-amber-900/10 transition">
                <div className="col-span-3 flex items-center gap-3 min-w-0">
                  <Avatar
                    src={r?.userId?.profileImage || r?.userId?.avatar}
                    name={r?.userId?.name}
                    size="sm"
                    ring
                  />
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 dark:text-white truncate">
                      {r?.userId?.name || "—"}
                    </div>
                    {r?.userId?.email && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {r?.userId?.email}
                      </div>
                    )}
                  </div>
                </div>


                {/* rating */}
                <div className="col-span-2 flex items-center">
                  <div className="flex items-center gap-2">
                    <Stars value={r?.rating} />
                    <span className="text-xs text-slate-500">({r?.rating || 0})</span>
                  </div>
                </div>

                {/* comment */}
                <div className="col-span-4 text-sm text-slate-700 dark:text-slate-300 line-clamp-2">
                  {r?.comment || "—"}
                </div>

                {/* date */}
                <div className="col-span-2 text-sm text-slate-600 dark:text-slate-400">
                  {fmtDate(r?.createdAt)}
                </div>

                {/* actions */}
                <div className="col-span-1 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setConfirm({ open: true, id: r._id })}
                    disabled={busyId === r._id}
                    className={cn(
                      `${AD.btnDanger} text-xs py-1.5`,
                      busyId === r._id && "opacity-60 cursor-wait"
                    )}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="border-t border-slate-100 px-6 py-14 text-center dark:border-gray-800">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">No reviews found</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Try adjusting search or filters.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-600 dark:text-slate-400">
          Showing <b>{paged.length || 0}</b> of <b>{total}</b>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={pageSafe === 1}
            className={cn(
              AD.btnSecondary,
              pageSafe === 1 && "opacity-50 cursor-not-allowed"
            )}
          >
            ‹ Prev
          </button>
          <div className="text-xs">
            Page {pageSafe} / {totalPages}
          </div>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={pageSafe === totalPages}
            className={cn(
              AD.btnSecondary,
              pageSafe === totalPages && "opacity-50 cursor-not-allowed"
            )}
          >
            Next ›
          </button>
        </div>
      </div>

      {/* Confirm Modal */}
      {confirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirm({ open: false, id: null })} />
          <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border dark:border-gray-800 shadow-xl p-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Delete Review</h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Are you sure you want to permanently delete this review?
            </p>
            <div className="mt-6 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleDelete(confirm.id)}
                className="inline-flex w-full items-center justify-center rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirm({ open: false, id: null })}
                className={`${AD.btnSecondary} w-full justify-center`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- small bits ----------------------------- */
function Chip({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 px-3 py-1 font-semibold">
      <span className="text-[11px] uppercase tracking-wide">{label}</span>
      <span className="text-sm">{value}</span>
    </span>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">{label}</div>
      <div className="relative mt-1">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={AD.select}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    </label>
  );
}

function Avatar({
  src,
  name = "",
  alt = "avatar",
  size = "sm",                 // "xs" | "sm" | "md" | "lg" | "xl"
  ring = false,                // amber ring
  status,                      // "online" | "idle" | "busy" | "offline" (optional)
  className = "",
  crossOrigin,
  referrerPolicy = "no-referrer",
}) {
  const [errored, setErrored] = React.useState(false);

  const initials = React.useMemo(() => {
    const s = String(name).trim();
    if (!s) return "U";
    const parts = s.split(/\s+/);
    const str =
      parts.length === 1
        ? parts[0].slice(0, 2)
        : (parts[0][0] || "") + (parts[parts.length - 1][0] || "");
    return str.toUpperCase();
  }, [name]);

  const sizeMap = {
    xs: "h-6 w-6 text-[10px]",
    sm: "h-9 w-9 text-xs",
    md: "h-10 w-10 text-sm",
    lg: "h-12 w-12 text-base",
    xl: "h-16 w-16 text-lg",
  };

  const ringCls = ring
    ? "ring-2 ring-amber-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900"
    : "";

  const baseCls = [
    sizeMap[size] || sizeMap.sm,
    "relative rounded-full overflow-hidden select-none",
    ringCls,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const statusColor =
    status === "online"
      ? "bg-emerald-500"
      : status === "idle"
      ? "bg-amber-400"
      : status === "busy"
      ? "bg-rose-500"
      : "bg-slate-400";

  const showImage = Boolean(src && !errored);

  return (
    <div className={baseCls} aria-label={name || alt}>
      {showImage ? (
        <img
          src={src}
          alt={alt}
          crossOrigin={crossOrigin}
          referrerPolicy={referrerPolicy}
          className="h-full w-full object-cover"
          onError={() => setErrored(true)}
          loading="lazy"
        />
      ) : (
        <div className="h-full w-full grid place-items-center font-bold
                        bg-gradient-to-br from-amber-100 to-amber-200 text-amber-900
                        dark:from-amber-200/20 dark:to-amber-300/10 dark:text-amber-200">
          {initials}
        </div>
      )}

      {status && (
        <span
          className={`absolute -right-0.5 -bottom-0.5 h-3.5 w-3.5 rounded-full border-2 border-white dark:border-gray-900 ${statusColor}`}
          aria-label={`status: ${status}`}
          title={status}
        />
      )}
    </div>
  );
}
